import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useProperty, resolveAsset } from '../context/PropertyContext';
import { Loader2 } from 'lucide-react';

/**
 * Handles the Emergent OAuth redirect landing.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 *
 * URL fragment on entry: `#session_id=<one-time-id>`. We exchange it for a
 * persistent session with the backend, then land on the app root.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');
  const { refresh } = useAuth() || {};
  const { property } = useProperty() || {};

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { nav('/login', { replace: true }); return; }
    const sessionId = decodeURIComponent(match[1]);
    // Client-side safety timeout so the loader never spins forever.
    const timeout = setTimeout(() => {
      setError('Sign-in is taking longer than expected. Please try again.');
    }, 15000);
    (async () => {
      try {
        await api.post('/auth/session', {}, { headers: { 'X-Session-ID': sessionId } });
        clearTimeout(timeout);
        window.history.replaceState({}, '', '/');
        await refresh?.();
        nav('/', { replace: true });
      } catch (e) {
        clearTimeout(timeout);
        setError(formatApiErrorDetail(e.response?.data?.detail) || 'Sign-in failed');
      }
    })();
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const propName = property?.name || 'Front Desk Console';
  const logoUrl = property?.logo_url ? resolveAsset(property.logo_url) : '';

  return (
    <div className="min-h-screen app-bg flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-8 text-center">
        <div className="flex items-center gap-2 justify-center mb-4">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="w-10 h-10 rounded-lg object-contain bg-white border border-[#CFD8CF]" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-[#2E7D32] text-white grid place-items-center font-heading font-bold" aria-hidden>
              {propName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="text-left">
            <div className="font-heading text-[#1F5A24] leading-tight">{propName}</div>
            <div className="text-[11px] uppercase tracking-widest text-[#333]/60">Front Desk Console</div>
          </div>
        </div>

        {error ? (
          <>
            <h1 data-testid="auth-callback-error" role="alert" className="font-heading text-xl text-[#B26A00]">Sign-in couldn't finish</h1>
            <p className="text-sm text-[#333]/80 mt-2">{error}</p>
            <button
              data-testid="auth-callback-back"
              onClick={() => nav('/login', { replace: true })}
              className="mt-5 w-full min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="animate-spin mx-auto text-[#2E7D32]" size={28} aria-hidden />
            <h1 data-testid="auth-callback-loading" className="font-heading text-xl text-[#1F5A24] mt-3">Signing you in…</h1>
            <p className="text-sm text-[#333]/70 mt-1">Just a moment.</p>
          </>
        )}
      </div>
    </div>
  );
}
